import camelcaseKeys from 'camelcase-keys'
import { RealtimePostgresChangesPayload } from '@supabase/realtime-js'
import { create } from 'zustand'
import { Database } from '../types/supabase'
import { supabase } from '../supabaseClient'
import { callTool } from '../lib/tools'
import useOrganizations from './useOrganizations'

export type OrgInvite = {
  id: string
  orgId: string
  email: string
  role: string
  status: string
  createdAt: string
  expiresAt: string
}

export interface OrgInvitesState {
  invites: OrgInvite[]
  set: (state: Partial<OrgInvitesState>) => void
  get: (orgId?: string) => Promise<void>
  add: (invite: OrgInvite) => void
  remove: (inviteId: string) => void
  invite: (orgId: string, email: string, role: string) => Promise<string>
  resend: (inviteId: string) => Promise<void>
  revoke: (inviteId: string) => Promise<void>
  subscribe: (orgId?: string) => () => void
  reset: () => void
}

const state = {
  invites: [],
}

const COLUMNS = 'id, org_id, email, role, status, created_at, expires_at'

// Pending invites are read straight from Supabase; role-gated writes route through callTool.
// ⚠ Only an org Owner or Admin may read them (PERMISSIONS.md § 5 Org, app#381). After router
// migration 0227 the invites_read RLS policy gives a Member no row, so the members page does not
// call get() or subscribe() for a Member. COLUMNS must stay inside the 0227 column grant
// (token_hash, accepted_by and accepted_at are not granted): a column outside it fails the read.
const useOrgInvites = create<OrgInvitesState>()((set, get) => ({
  ...state,
  set,
  get: async orgId => {
    if (!orgId) return
    const { data, error } = await supabase
      .from('organization_invites')
      .select(COLUMNS)
      .eq('org_id', orgId)
      .eq('status', 'pending')
      .order('created_at', { ascending: false })
    if (error) {
      console.warn(error)
      return
    }
    const invites = data ? (data.map(i => camelcaseKeys(i)) as OrgInvite[]) : []
    // ⚠ Drop an answer for an org that is not active now (app#408), as useOrgMembers.get does.
    // Without this, the pending invites of the previous org show after an org switch.
    if (useOrganizations.getState().activeOrgId !== orgId) return
    set({ invites })
  },
  add: invite => {
    const invites = [...get().invites]
    if (invites.some(i => i.id === invite.id)) return
    invites.unshift(invite)
    set({ invites })
  },
  remove: inviteId => {
    set({ invites: get().invites.filter(i => i.id !== inviteId) })
  },
  invite: async (orgId, email, role) => {
    const res = await callTool<{ invite_url: string }>('org_invite_create', {
      org_id: orgId,
      email,
      role,
    })
    await get().get(orgId)
    return res.invite_url
  },
  resend: async inviteId => {
    // Rotates the token + re-emails server-side; the row stays pending, so no
    // local list change is needed.
    await callTool('org_invite_resend', { invite_id: inviteId })
  },
  revoke: async inviteId => {
    // Optimistic removal — the invite leaves the pending list immediately; the
    // realtime UPDATE (status → revoked) reconciles, and a failure refetches.
    const previous = get().invites
    const orgId = useOrganizations.getState().activeOrgId
    get().remove(inviteId)
    try {
      await callTool('org_invite_revoke', { invite_id: inviteId })
    } catch (error) {
      console.warn('Revoke invite error:', error)
      // ⚠ Only for the org it was taken from: after an org switch, `previous` is the old org's list.
      if (useOrganizations.getState().activeOrgId === orgId) set({ invites: previous })
      throw error
    }
  },
  subscribe: orgId => {
    if (!orgId) return () => {}
    const { add, remove } = get()
    const subscription = supabase
      .channel(`organization_invites-${orgId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'organization_invites', filter: `org_id=eq.${orgId}` },
        (payload: RealtimePostgresChangesPayload<Database['public']['Tables']['organization_invites']['Row']>) => {
          switch (payload.eventType) {
            case 'INSERT': {
              const invite = camelcaseKeys(payload.new) as OrgInvite
              if (invite.status === 'pending') add(invite)
              break
            }
            case 'UPDATE': {
              // A pending invite that transitions to accepted/revoked/expired
              // leaves the pending list.
              const invite = camelcaseKeys(payload.new) as OrgInvite
              if (invite.status !== 'pending') remove(invite.id)
              break
            }
            case 'DELETE':
              if (payload.old.id) remove(payload.old.id)
              break
          }
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(subscription)
    }
  },
  reset: () => set({ ...state }),
}))

export default useOrgInvites
